// Places already written somewhere in a tree, offered while typing a place so spellings stay
// the same, and where they are on the map. Nothing here leaves the device; online lookup is
// src/io/nominatim.ts.

import { normalize } from './search';
import type { Id, LifeEvent, PartnerRel, PlaceLocation, Tree } from './types';

export interface TreePlace {
  place: string;
  /** How many events, links, and sources use it. */
  count: number;
  /** normalize(place), for matching. */
  key: string;
}

const cache = new WeakMap<Tree, TreePlace[]>();

/** Every place in the tree, once each (the most used spelling), most used first. */
export function treePlaces(tree: Tree): TreePlace[] {
  const hit = cache.get(tree);
  if (hit) return hit;
  const byKey = new Map<string, Map<string, number>>();
  const add = (place: string | undefined) => {
    const p = place?.trim().replace(/\s+/g, ' ');
    if (!p) return;
    const key = normalize(p);
    if (!key) return;
    const spellings = byKey.get(key) ?? new Map<string, number>();
    spellings.set(p, (spellings.get(p) ?? 0) + 1);
    byKey.set(key, spellings);
  };
  for (const p of Object.values(tree.people)) for (const e of p.events) add(e.place);
  for (const r of Object.values(tree.relationships)) add(r.place);
  for (const s of Object.values(tree.sources ?? {})) add(s.place);
  const list: TreePlace[] = [];
  for (const [key, spellings] of byKey) {
    let place = '';
    let best = 0;
    let count = 0;
    for (const [s, n] of spellings) {
      count += n;
      if (n > best) [place, best] = [s, n];
    }
    list.push({ place, count, key });
  }
  list.sort((a, b) => b.count - a.count || a.place.localeCompare(b.place));
  cache.set(tree, list);
  return list;
}

/**
 * Places matching what's been typed: each word typed starts a word of the place, in any order
 * ("spring ill" finds "Springfield, Illinois"), or the text appears inside it. Places that start
 * with the text come first, then the most used. The place exactly as typed is left out.
 */
export function matchPlaces(places: TreePlace[], query: string, limit = 6): TreePlace[] {
  const q = normalize(query);
  if (!q) return [];
  const terms = q.split(' ');
  const scored: { p: TreePlace; score: number }[] = [];
  for (const p of places) {
    if (p.key === q) continue;
    let score: number;
    if (p.key.startsWith(q)) score = 0;
    else {
      const words = p.key.split(' ');
      if (terms.every((t) => words.some((w) => w.startsWith(t)))) score = 1;
      else if (p.key.includes(q)) score = 2;
      else continue;
    }
    scored.push({ p, score });
  }
  // `places` is already most-used first, and sort is stable.
  return scored
    .sort((a, b) => a.score - b.score)
    .slice(0, limit)
    .map((s) => s.p);
}

/** What a place is known by in `tree.locations`: spellings that differ only in case, accents, or punctuation share it. */
export const placeKey = (place: string) => normalize(place);

export function locationOf(tree: Tree, place: string | undefined): PlaceLocation | undefined {
  return place ? tree.locations?.[placeKey(place)] : undefined;
}

/** The tree with these places' locations set, or forgotten (null). The same tree when nothing changes. */
export function withLocations(tree: Tree, changes: Iterable<[string, PlaceLocation | null]>): Tree {
  const locations = { ...tree.locations };
  let changed = false;
  for (const [place, loc] of changes) {
    const key = placeKey(place);
    if (!key) continue;
    const old = locations[key];
    if (loc ? old?.lat === loc.lat && old?.lon === loc.lon : !old) continue;
    if (loc) locations[key] = { lat: loc.lat, lon: loc.lon };
    else delete locations[key];
    changed = true;
  }
  return changed ? { ...tree, locations, updatedAt: new Date().toISOString() } : tree;
}

/** The kinds of thing the map can show, each with its own color and filter. */
export type MapKind = 'birth' | 'death' | 'home' | 'partnership' | 'move' | 'other';
export const MAP_KINDS: { kind: MapKind; label: string }[] = [
  { kind: 'birth', label: 'Births' },
  { kind: 'death', label: 'Deaths & burials' },
  { kind: 'home', label: 'Homes' },
  { kind: 'partnership', label: 'Partnerships' },
  { kind: 'move', label: 'Moves' },
  { kind: 'other', label: 'Other events' },
];

const KIND_OF: Partial<Record<LifeEvent['type'], MapKind>> = {
  birth: 'birth',
  death: 'death',
  burial: 'death',
  cremation: 'death',
  residence: 'home',
  census: 'home',
  immigration: 'move',
  emigration: 'move',
  naturalization: 'move',
};

/** Something that happened at a place: a person's event, or a partnership. */
export type MapEntry = { kind: MapKind; date?: string; personIds: Id[] } & ({ event: LifeEvent } | { rel: PartnerRel });

export interface MapPlace {
  key: string;
  /** The most used spelling. */
  place: string;
  location?: PlaceLocation;
  entries: MapEntry[];
}

/** Every place where someone's event or a partnership happened, with what happened there. */
export function mapPlaces(tree: Tree): MapPlace[] {
  const byKey = new Map<string, MapPlace & { spellings: Map<string, number> }>();
  const add = (place: string | undefined, entry: MapEntry) => {
    const p = place?.trim().replace(/\s+/g, ' ');
    const key = p && placeKey(p);
    if (!p || !key) return;
    let mp = byKey.get(key);
    if (!mp) byKey.set(key, (mp = { key, place: p, location: tree.locations?.[key], entries: [], spellings: new Map() }));
    mp.entries.push(entry);
    mp.spellings.set(p, (mp.spellings.get(p) ?? 0) + 1);
  };
  for (const person of Object.values(tree.people))
    for (const event of person.events) add(event.place, { kind: KIND_OF[event.type] ?? 'other', date: event.date, personIds: [person.id], event });
  for (const rel of Object.values(tree.relationships)) if (rel.kind === 'partner') add(rel.place, { kind: 'partnership', date: rel.start, personIds: [rel.a, rel.b], rel });
  return [...byKey.values()].map(({ spellings, ...mp }) => ({ ...mp, place: [...spellings].sort((a, b) => b[1] - a[1])[0][0] }));
}
